import { useToast } from "@chakra-ui/toast";
import { FormikConfig, FormikHelpers } from "formik";
import { FC, useCallback } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { useNavigate } from "react-router-dom";

import { ScheduleEditFormContainer_Mutation } from "~/src/__relay_artifacts__/ScheduleEditFormContainer_Mutation.graphql";
import { ScheduleEditFormContainer_Query } from "~/src/__relay_artifacts__/ScheduleEditFormContainer_Query.graphql";
import { useFormErrorHandler } from "~/src/lib/hooks";
import { useMutationCommit } from "~/src/lib/react-relay";
import { formatTimeToISO } from "~/src/lib/utils/date-utils";

import { ScheduleForm, ScheduleFormValues } from "./";

type Props = {
  siteSlug: string;
  scheduleSlug: string;
};

const query = graphql`
  query ScheduleEditFormContainer_Query($scheduleSlug: String!) {
    schedule(slug: $scheduleSlug) {
      id
      slug
      startTime
      endTime
    }
  }
`;

const mutation = graphql`
  mutation ScheduleEditFormContainer_Mutation($input: UpdateScheduleInput!) {
    updateSchedule(input: $input) {
      schedule {
        id
        slug
        startTime
        endTime
      }
    }
  }
`;

export const ScheduleEditFormContainer: FC<Props> = ({
  siteSlug,
  scheduleSlug,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { onFormError } = useFormErrorHandler();

  const { schedule } = useLazyLoadQuery<ScheduleEditFormContainer_Query>(
    query,
    {
      scheduleSlug,
    }
  );

  const updateScheduleMutation =
    useMutationCommit<ScheduleEditFormContainer_Mutation>(mutation);

  const initialValues: ScheduleFormValues = {
    startTime: schedule?.startTime ? new Date(schedule.startTime) : null,
    endTime: schedule?.endTime ? new Date(schedule.endTime) : null,
  };

  const handleSubmit = useCallback<
    FormikConfig<ScheduleFormValues>["onSubmit"]
  >(
    async (
      values: ScheduleFormValues,
      { setErrors }: FormikHelpers<ScheduleFormValues>
    ) => {
      try {
        if (!schedule) return;

        const { updateSchedule } = await updateScheduleMutation({
          variables: {
            input: {
              scheduleId: schedule.id,
              startTime: formatTimeToISO(values.startTime),
              endTime: formatTimeToISO(values.endTime),
            },
          },
        });
        if (!updateSchedule?.schedule) throw new Error("assertion failed");
        toast({ title: "スケジュールを更新しました", status: "success" });
        navigate(`/sites/${siteSlug}/settings?tab=schedules`);
      } catch (err) {
        onFormError(err as any, setErrors);
      }
    },
    [navigate, onFormError, schedule, siteSlug, toast, updateScheduleMutation]
  );

  const handleCancel = useCallback(() => {
    navigate(`/sites/${siteSlug}/settings?tab=schedules`);
  }, [navigate, siteSlug]);

  return (
    <ScheduleForm
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    />
  );
};
